/**
 * Per-supplier Call-Off configuration hook.
 *
 * Fetches GET /api/v1/call-off/supplier-config/current/ once and caches the
 * result for the whole tab. The endpoint always returns a payload — falls back
 * to the historical hardcoded defaults when a supplier has no config row yet —
 * so callers can rely on `config` being non-null after the initial load.
 */
import { useEffect, useRef, useState } from 'react';
import { callOffAPI } from '../lib/api';
import type { CallOffSupplierConfig } from '../lib/api';

const FALLBACK_CONFIG: CallOffSupplierConfig = {
  id: null,
  supplier: null,
  supplier_name: null,
  persisted: false,
  direct_delivery_enabled: true,
  polish_factory_subtab_enabled: true,
  primary_warehouse_label: 'Saltney',
  primary_warehouse_column_label: 'Sum On Saltney',
  routing_origin: 'Dover, UK',
  direct_delivery_modal_title: 'Add Direct Deliveries',
  source_label_singular: 'Factory',
  source_label_plural: 'Factories',
  direct_delivery_sources: [],
};

let cachedConfig: CallOffSupplierConfig | null = null;
let inflight: Promise<CallOffSupplierConfig> | null = null;

async function loadConfig(): Promise<CallOffSupplierConfig> {
  if (cachedConfig) return cachedConfig;
  if (inflight) return inflight;
  inflight = (async () => {
    try {
      const res = await callOffAPI.getSupplierConfig();
      cachedConfig = res.data;
      return res.data;
    } catch {
      // If the call fails (e.g. no auth, network), fall back to the
      // historical defaults so call-off UI still renders. Re-fetched on
      // next mount via reset().
      return FALLBACK_CONFIG;
    } finally {
      inflight = null;
    }
  })();
  return inflight;
}

/**
 * Read-only access to the current supplier's Call-Off configuration.
 *
 * @returns `{ config, loading }` — `config` is the fallback defaults until
 * the network request resolves, then the live values.
 */
export function useCallOffConfig(): { config: CallOffSupplierConfig; loading: boolean } {
  const [config, setConfig] = useState<CallOffSupplierConfig>(cachedConfig ?? FALLBACK_CONFIG);
  const [loading, setLoading] = useState<boolean>(!cachedConfig);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    if (cachedConfig) {
      setConfig(cachedConfig);
      setLoading(false);
      return;
    }
    loadConfig().then((c) => {
      if (mountedRef.current) {
        setConfig(c);
        setLoading(false);
      }
    });
    return () => { mountedRef.current = false; };
  }, []);

  return { config, loading };
}

/**
 * Drop the in-memory cache so the next consumer re-fetches. Call after
 * admin edits so call-off UIs pick up new labels/sources without a reload.
 */
export function resetCallOffConfigCache(): void {
  cachedConfig = null;
  inflight = null;
}
